import React, { type FC, useEffect, useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import {
  EditableUrlSlot,
  useGlobalMessageHandler,
  useUsUrlDescTypeLuts
} from 'components'
import {
  createExecutiveOrgUrl,
  deleteExecutiveOrgUrl,
  type ExecOrgUrl,
  listExecutiveOrgUrls,
  updateExecutiveOrgUrl,
  type UsUrlRoleDescType
} from 'services'

interface ExecutiveOrgUrlEditorProps {
  orgId: number
  roleId: number
  onWebsiteStatus?: (hasWebsite: boolean) => void
}

const shortUrlLabel = (descType: UsUrlRoleDescType): string => {
  const roleDesc = descType.role.desc
  return descType.desc.startsWith(roleDesc) ? descType.desc.slice(roleDesc.length).trim() : descType.desc
}

const emptyUrl = (orgId: number, descType: UsUrlRoleDescType): ExecOrgUrl => ({
  id: -1,
  execOrgId: orgId,
  typeId: descType.type.id,
  descId: descType.id,
  value: '',
  displayOrder: 1,
  lastUpdated: new Date().toISOString()
})

export const ExecutiveOrgUrlEditor: FC<ExecutiveOrgUrlEditorProps> = ({ orgId, roleId, onWebsiteStatus }) => {
  const { lutValues: descTypeValues } = useUsUrlDescTypeLuts()
  const { showErrorMessage } = useGlobalMessageHandler()
  const [urls, setUrls] = useState<ExecOrgUrl[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    listExecutiveOrgUrls(orgId)
      .then(setUrls)
      .catch(() => { showErrorMessage('Failed to load organization URLs.') })
      .finally(() => { setLoading(false) })
  }, [orgId, showErrorMessage])

  const allDescTypes = Array.from(descTypeValues.descTypes.values())
  const contentTypeById = new Map(allDescTypes.map(descType => [descType.id, descType.contentType]))

  // A role change leaves the desc behind, so slots bind by content type, not descId.
  const matchesForDescType = (descType: UsUrlRoleDescType): ExecOrgUrl[] => {
    if (descType.contentType == null) return urls.filter(url => url.descId === descType.id)
    return urls.filter(url => contentTypeById.get(url.descId) === descType.contentType)
  }

  const hasWebsite = urls.some(
    url => contentTypeById.get(url.descId) === 'website' && url.value.trim() !== ''
  )
  useEffect(() => {
    if (!loading) onWebsiteStatus?.(hasWebsite)
  }, [loading, hasWebsite, onWebsiteStatus])

  const handleSaved = (saved: ExecOrgUrl): void => {
    setUrls(prev => {
      const without = prev.filter(u => u.id !== saved.id)
      return [...without, saved]
    })
  }

  const handleRemoved = (removed: ExecOrgUrl): void => {
    setUrls(prev => prev.filter(u => u.id !== removed.id))
  }

  if (loading) {
    return <Box display='flex' justifyContent='center'><CircularProgress /></Box>
  }

  const descTypes = allDescTypes
    .filter(descType => descType.role.id === roleId)
    .sort((a, b) => a.lutOrder - b.lutOrder)

  return (
    <Stack spacing={2}>
      {descTypes.length === 0
        ? <Box>No URL types available for this organization.</Box>
        : descTypes.map(descType => {
          const matches = matchesForDescType(descType)
          return (
            <Box key={descType.id}>
              <EditableUrlSlot<ExecOrgUrl>
                descType={descType}
                url={matches.find(url => url.descId === descType.id) ?? matches[0] ?? null}
                label={shortUrlLabel(descType)}
                createEmptyUrl={() => emptyUrl(orgId, descType)}
                onSave={async u => await (u.id <= 0 ? createExecutiveOrgUrl(orgId, u) : updateExecutiveOrgUrl(orgId, u.id, u.value))}
                onDelete={async u => await deleteExecutiveOrgUrl(orgId, u.id)}
                onSaved={handleSaved}
                onRemoved={handleRemoved}
              />
              {matches.length > 1 && (
                <Typography variant='caption' color='warning.main'>
                  {matches.length} URLs of this type exist on this organization; only the one shown can be edited here.
                </Typography>
              )}
            </Box>
          )
        })}
    </Stack>
  )
}
